import { useState, useEffect } from 'react';
import { suggestPricing } from '@/lib/ai-preview';

interface PricingCalculatorProps {
  cost: number;
  price: number;
  onCostChange: (cost: number) => void;
  onPriceChange: (price: number) => void;
}

export default function PricingCalculator({ cost, price, onCostChange, onPriceChange }: PricingCalculatorProps) {
  const [margin, setMargin] = useState(0);
  const [markup, setMarkup] = useState(0);
  const [profit, setProfit] = useState(0);
  const [gst, setGst] = useState(0);
  const [priceWithGst, setPriceWithGst] = useState(0);
  const [aiSuggestion, setAiSuggestion] = useState<{ suggestedPrice: number; margin: number } | null>(null);
  const [category] = useState('');

  useEffect(() => {
    if (price > 0) {
      const profitVal = price - cost;
      setProfit(profitVal);
      setMargin(price > 0 ? (profitVal / price) * 100 : 0);
      setMarkup(cost > 0 ? (profitVal / cost) * 100 : 0);
      const gstVal = price * 0.09;
      setGst(gstVal);
      setPriceWithGst(price + gstVal);
    } else {
      setProfit(0);
      setMargin(0);
      setMarkup(0);
      setGst(0);
      setPriceWithGst(0);
    }
  }, [cost, price]);

  const handleCostChange = (val: number) => {
    onCostChange(val);
  };

  const handlePriceChange = (val: number) => {
    onPriceChange(val);
  };

  const handleMarginChange = (val: number) => {
    if (cost > 0) {
      const newPrice = cost / (1 - val / 100);
      onPriceChange(Math.round(newPrice * 100) / 100);
    }
  };

  const handleAiSuggest = async () => {
    if (cost > 0) {
      const suggestion = await suggestPricing(cost, category);
      setAiSuggestion(suggestion);
    }
  };

  const applyAiSuggestion = () => {
    if (aiSuggestion) {
      onPriceChange(aiSuggestion.suggestedPrice);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <label className="label-field">Cost Price (SGD)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={cost || ''}
            onChange={(e) => handleCostChange(parseFloat(e.target.value) || 0)}
            className="input-field mt-1"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="label-field">Retail Price (SGD)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price || ''}
            onChange={(e) => handlePriceChange(parseFloat(e.target.value) || 0)}
            className="input-field mt-1"
            placeholder="0.00"
          />
        </div>
      </div>

      <div>
        <label className="label-field">Target Margin: {margin.toFixed(1)}%</label>
        <input
          type="range"
          min="0"
          max="90"
          step="1"
          value={margin}
          onChange={(e) => handleMarginChange(parseFloat(e.target.value))}
          className="mt-1 w-full"
        />
      </div>

      <div className="rounded-lg border border-border p-4">
        <h4 className="mb-3 text-sm font-semibold text-foreground">Pricing Breakdown</h4>
        <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <p className="text-muted-foreground">Profit</p>
            <p className={`text-lg font-semibold ${profit >= 0 ? 'text-success' : 'text-destructive'}`}>
              SGD {profit.toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Margin</p>
            <p className="text-lg font-semibold text-foreground">{margin.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-muted-foreground">Markup</p>
            <p className="text-lg font-semibold text-foreground">{markup.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-muted-foreground">GST (9%)</p>
            <p className="text-lg font-semibold text-foreground">SGD {gst.toFixed(2)}</p>
          </div>
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">Price incl. GST</p>
          <p className="text-xl font-bold text-foreground">SGD {priceWithGst.toFixed(2)}</p>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-foreground">AI Pricing Suggestion</h4>
            {aiSuggestion && (
              <p className="mt-1 text-sm text-muted-foreground">
                Suggested: SGD {aiSuggestion.suggestedPrice.toFixed(2)} ({aiSuggestion.margin}% margin)
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleAiSuggest} className="btn-secondary">
              Get Suggestion
            </button>
            {aiSuggestion && (
              <button type="button" onClick={applyAiSuggestion} className="btn-primary">
                Apply
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
